import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Table, ListGroup, Badge, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { getPlannedClassFullDetail, addCommentToPlannedClass, updatePlannedClassApproval, authHeaders } from '../../api';
import { ENDPOINTS } from '../../endpoints';

const pmStyles = `
.pm-modal .modal-header {
  background: linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%);
  color: #fff;
  border-bottom: none;
  padding: 1rem 1.5rem;
}
.pm-modal .modal-title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #fff;
}
.pm-modal .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.85;
}
.pm-modal .modal-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}
.pm-section {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0369a1;
  margin: 1.25rem 0 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 2px solid #e0f2fe;
}
.pm-stage-thead th {
  background: #dbeafe !important;
  color: #1e40af !important;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 2px solid #bfdbfe !important;
}
.pm-stage-name {
  background: #f0f9ff !important;
  font-weight: 600;
  color: #0369a1;
  font-size: 0.85rem;
}
.pm-stage-dur {
  background: #f0f9ff !important;
  color: #64748b;
  font-size: 0.85rem;
  text-align: center;
}
.pm-comment-card {
  border-left: 3px solid #0ea5e9 !important;
  border-radius: 6px !important;
  padding: 0.6rem 0.85rem !important;
  background: #f8fafc !important;
  border-top: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.pm-comment-card + .pm-comment-card {
  margin-top: 0.4rem;
}
.pm-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  color: #0369a1;
  text-decoration: none;
}
`;

function usePMStyles() {
  useEffect(() => {
    if (document.getElementById('pm-modal-styles')) return;
    const tag = document.createElement('style');
    tag.id = 'pm-modal-styles';
    tag.textContent = pmStyles;
    document.head.appendChild(tag);
  }, []);
}

function SyncedReadonlyTextareas({ actividadValue, recursoValue }) {
  const refA = useRef(null);
  const refR = useRef(null);

  const sync = useCallback(() => {
    const elA = refA.current;
    const elR = refR.current;
    if (!elA || !elR) return;
    elA.style.height = 'auto';
    elR.style.height = 'auto';
    const maxH = Math.max(elA.scrollHeight, elR.scrollHeight);
    elA.style.height = `${maxH}px`;
    elR.style.height = `${maxH}px`;
  }, []);

  useEffect(() => { sync(); }, [actividadValue, recursoValue, sync]);

  const style = { resize: 'none', overflow: 'hidden', minHeight: '4rem' };

  return (
    <>
      <td>
        <textarea
          ref={refA}
          className="form-control bg-light border-0"
          value={actividadValue}
          readOnly
          rows={2}
          style={style}
          onChange={() => {}}
        />
      </td>
      <td>
        <textarea
          ref={refR}
          className="form-control bg-light border-0"
          value={recursoValue}
          readOnly
          rows={2}
          style={style}
          onChange={() => {}}
        />
      </td>
    </>
  );
}

const TYPE_BADGE = {
  'Aplicación del OA':   { bg: 'primary',   label: 'Aplicación del OA' },
  'Evaluación Formal':   { bg: 'danger',    label: 'Evaluación Formal' },
  'Evaluación Auténtica':{ bg: 'warning',   label: 'Evaluación Auténtica' },
};

const APPROVAL_BADGE = {
  'Por aprobar': { bg: 'info',     label: 'Por aprobar' },
  'Aprobada':    { bg: 'success',  label: 'Aprobada' },
  'Rechazada':   { bg: 'danger',   label: 'Rechazada' },
};

const FeedbackModal = ({ show, handleClose, onCommentAdded, plannedClassId }) => {
  usePMStyles();
  const { user } = useAuth();
  const [detail, setDetail]       = useState(null);
  const [classStages, setClassStages] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [newComment, setNewComment]           = useState('');
  const [newCommentTitle, setNewCommentTitle] = useState('');
  const [saving, setSaving]                   = useState(false);
  const [saveError, setSaveError]             = useState(null);
  const [approving, setApproving]             = useState(false);
  const [approvalError, setApprovalError]     = useState(null);

  // Cargar detalle + etapas configuradas cuando el modal se abre
  useEffect(() => {
    if (show && plannedClassId) {
      fetchAll();
    } else {
      setDetail(null);
      setError(null);
      setNewComment('');
      setNewCommentTitle('');
      setSaveError(null);
      setApprovalError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, plannedClassId]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailData, stagesRes] = await Promise.all([
        getPlannedClassFullDetail(plannedClassId),
        fetch(ENDPOINTS.ADMIN_CLASS_STAGES, { headers: authHeaders() }).then(r => r.ok ? r.json() : [])
      ]);
      if (!detailData) {
        setError('No se encontró la clase planificada.');
      } else {
        setDetail(detailData);
      }
      if (Array.isArray(stagesRes)) {
        setClassStages(stagesRes.sort((a, b) => a.display_order - b.display_order));
      }
    } catch (err) {
      setError('Error al cargar el detalle de la clase.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    if (!user?.organization_person_role_id) {
      setSaveError('No se pudo identificar tu usuario. Recarga la página.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const newCommentData = await addCommentToPlannedClass(
        plannedClassId,
        newCommentTitle.trim(),
        newComment.trim()
      );
      setDetail(prev => ({
        ...prev,
        comments: [...(prev.comments || []), {
          id: newCommentData.id,
          title: newCommentData.title || newCommentTitle.trim(),
          author: newCommentData.author || user.full_name || 'Jefe UTP',
          content: newCommentData.content,
          timestamp: newCommentData.timestamp
        }]
      }));
      setNewComment('');
      setNewCommentTitle('');
      if (onCommentAdded) onCommentAdded(detail);
    } catch (err) {
      setSaveError(err.message || 'Error al guardar el comentario.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproval = async (newStatus) => {
    setApproving(true);
    setApprovalError(null);
    try {
      await updatePlannedClassApproval(plannedClassId, newStatus);
      setDetail(prev => ({ ...prev, approval_status: newStatus }));
      if (onCommentAdded) onCommentAdded(detail);
    } catch (err) {
      setApprovalError(err.message || 'Error al actualizar el estado de aprobación.');
    } finally {
      setApproving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // dateStr es un string "YYYY-MM-DD" (sin hora): parsearlo directo con
      // `new Date(dateStr)` lo interpreta como medianoche UTC, y al convertir
      // a hora local en un huso detrás de UTC retrocede un día en pantalla.
      return new Date(`${dateStr}T00:00:00`).toLocaleDateString('es-CL', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch { return dateStr; }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('es-CL'); }
    catch { return ''; }
  };

  // Extrae el objeto de etapas (soporta { etapas: {...} } y el objeto directo)
  const resolveStages = (raw) => {
    if (!raw) return {};
    if (raw.etapas && typeof raw.etapas === 'object') return raw.etapas;
    return raw;
  };

  // Devuelve el dato de una etapa según su display_order
  const getStageData = (stagesObj, order) => {
    if (stagesObj[order]) return stagesObj[order];
    // fallback: buscar por campo 'orden' dentro de los valores
    const found = Object.values(stagesObj).find(
      v => v && typeof v === 'object' && v.orden === order
    );
    return found || {};
  };

  const typeBadge = detail ? (TYPE_BADGE[detail.class_type] || { bg: 'secondary', label: detail.class_type || '-' }) : null;
  const approvalBadge = detail ? (APPROVAL_BADGE[detail.approval_status] || { bg: 'secondary', label: detail.approval_status || 'Por aprobar' }) : null;
  const stagesObj = detail ? resolveStages(detail.stages) : {};

  return (
    <Modal show={show} onHide={handleClose} size="xl" className="pm-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Detalle de Planificación
          {detail && ` — ${formatDate(detail.class_date)}`}
          {detail && ` — ${detail.concept_keyword || detail.oa_code}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Cargando */}
        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          </div>
        )}

        {/* Error */}
        {error && !loading && <Alert variant="danger">{error}</Alert>}

        {detail && !loading && (
          <>
            {/* ── Estado de aprobación ── */}
            <div className="mb-3 p-3 border rounded d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <strong>Estado de aprobación:</strong>
                {approvalBadge && (
                  <Badge bg={approvalBadge.bg} style={{ fontSize: '0.9rem' }}>
                    {approvalBadge.label}
                  </Badge>
                )}
              </div>
              {approvalError && <small className="text-danger ms-2">{approvalError}</small>}
              {user?.user_type === 'utp' && (
                <div className="d-flex gap-2">
                  {detail.approval_status !== 'Aprobada' && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleApproval('Aprobada')}
                      disabled={approving}
                    >
                      {approving ? <Spinner animation="border" size="sm" /> : 'Aprobar'}
                    </Button>
                  )}
                  {detail.approval_status !== 'Rechazada' && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleApproval('Rechazada')}
                      disabled={approving}
                    >
                      {approving ? <Spinner animation="border" size="sm" /> : 'Rechazar'}
                    </Button>
                  )}
                  {detail.approval_status !== 'Por aprobar' && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleApproval('Por aprobar')}
                      disabled={approving}
                    >
                      Restablecer
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* ── Información general ── */}
            <div className="pm-section">Información General</div>
            <Row className="mb-3 g-2">
              <Col md={6}>
                <div className="p-3 border rounded bg-light h-100">
                  <p className="mb-1"><strong>ID de la Clase:</strong> {plannedClassId}</p>
                  <p className="mb-1"><strong>Docente:</strong> {detail.teacher_name}</p>
                  <p className="mb-1"><strong>Curso:</strong> {detail.course_name}</p>
                  <p className="mb-0"><strong>Asignatura:</strong> {detail.subject_name}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-3 border rounded bg-light h-100">
                  <p className="mb-1">
                    <strong>OA: </strong>
                    <Badge bg="primary" className="me-1">{detail.oa_code}</Badge>
                  </p>
                  <p className="mb-1 text-muted small">{detail.oa_statement}</p>
                  <p className="mb-0">
                    <strong>Tipo: </strong>
                    {typeBadge && <Badge bg={typeBadge.bg}>{typeBadge.label}</Badge>}
                  </p>
                </div>
              </Col>
            </Row>

            {/* ── Objetivo ── */}
            <Form.Group className="mb-3">
              <div className="pm-section">Objetivo de la Clase</div>
              <Form.Control
                as="textarea"
                rows={2}
                value={detail.objective || ''}
                readOnly
                className="bg-light"
              />
            </Form.Group>

            {/* ── Etapas ── */}
            <div className="pm-section">Etapas de la Clase</div>
            {classStages.length === 0 ? (
              <p className="text-muted">Sin información de etapas configuradas.</p>
            ) : (
              <Table bordered responsive className="mb-3">
                <thead className="pm-stage-thead">
                  <tr>
                    <th style={{ width: '20%' }}>Etapa</th>
                    <th style={{ width: '15%' }}>Duración (min)</th>
                    <th style={{ width: '35%' }}>Actividad</th>
                    <th style={{ width: '30%' }}>Recurso</th>
                  </tr>
                </thead>
                <tbody>
                  {classStages.map(stage => {
                    const sd = getStageData(stagesObj, stage.display_order);
                    return (
                      <tr key={stage.class_stage_id}>
                        <td className="align-middle pm-stage-name"><strong>{stage.name}</strong></td>
                        <td className="align-middle pm-stage-dur">{stage.duration_minutes}</td>
                        <SyncedReadonlyTextareas
                          actividadValue={sd.actividad || sd.actividades || ''}
                          recursoValue={sd.recurso || sd.recursos || ''}
                        />
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            {/* ── Adjuntos ── */}
            <Form.Group className="mb-3">
              <div className="pm-section">Archivos Adjuntos</div>
              {detail.attachments && detail.attachments.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {detail.attachments.map((f, i) => (
                    f.url ? (
                      <a
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="pm-file-chip"
                      >
                        📎 {f.filename || f.stored_filename || `Archivo ${i + 1}`}
                      </a>
                    ) : (
                      <span key={i} className="pm-file-chip">
                        📎 {f.filename || f.stored_filename || `Archivo ${i + 1}`}
                      </span>
                    )
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Sin archivos adjuntos.</p>
              )}
            </Form.Group>

            {/* ── Comentarios UTP ── */}
            <div className="pm-section">Comentarios del Jefe UTP</div>
            <ListGroup className="mb-3">
              {detail.comments && detail.comments.length > 0 ? (
                detail.comments.map((c, i) => (
                  <ListGroup.Item key={c.id || i} className="pm-comment-card">
                    <div>
                      <strong>{c.title || 'Comentario'}</strong>
                      {' - '}
                      <small className="text-muted">{c.author}</small>
                    </div>
                    <div>{c.content}</div>
                    {c.timestamp && (
                      <small className="text-muted">{formatTimestamp(c.timestamp)}</small>
                    )}
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item className="text-muted">No hay comentarios aún.</ListGroup.Item>
              )}
            </ListGroup>

            {/* ── Agregar comentario ── */}
            <div className="pm-section">Agregar Comentario</div>
            {saveError && <Alert variant="danger" className="py-2">{saveError}</Alert>}
            <Row className="mb-2">
              <Col md={4}>
                <Form.Control
                  type="text"
                  placeholder="Título del comentario"
                  value={newCommentTitle}
                  onChange={(e) => setNewCommentTitle(e.target.value)}
                  disabled={saving}
                />
              </Col>
              <Col md={8}>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Escriba su comentario..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  disabled={saving}
                />
              </Col>
            </Row>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleAddComment}
              disabled={saving || !newComment.trim() || !newCommentTitle.trim()}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Guardando...
                </>
              ) : 'Añadir Comentario'}
            </Button>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FeedbackModal;
